/**
 * List of sensitive query parameter keys that should be removed before logging to external services.
 * These parameters may contain authorization codes, secrets, tokens, or other sensitive data.
 */
const SENSITIVE_QUERY_PARAMS = [
	'code' /** TrueLayer authorization code */,
	'setup_intent_client_secret' /** Stripe setup intent secret */,
	'paymentMethodId' /** Stripe payment method ID (potentially sensitive) */,
	'client_secret' /** Generic client secret */,
] as const;

/**
 * Sanitizes query parameters by removing sensitive data before logging to external services like Sentry.
 *
 * @param query - URLSearchParams object or query string to sanitize
 * @returns Sanitized query string safe for logging, with sensitive parameters replaced with [REDACTED]
 *
 * @example
 * const query = new URLSearchParams('code=auth123&redirect_status=success');
 * const safe = sanitizeQueryForLogging(query);
 * // Returns: "code=[REDACTED]&redirect_status=success"
 */
export function sanitizeQueryForLogging(
	query: URLSearchParams | string
): string {
	const params = typeof query === 'string' ? new URLSearchParams(query) : query;
	const sanitized = new URLSearchParams(params);

	SENSITIVE_QUERY_PARAMS.forEach((key) => {
		if (sanitized.has(key)) {
			sanitized.set(key, '[REDACTED]');
		}
	});

	return sanitized.toString();
}
